import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BsDownload } from "react-icons/bs";
import { IoPrint } from "react-icons/io5";
import { FaWhatsapp } from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Header1 from '../Header1/Header1';
import axiosInstance from "../../lib/axiosInstance";
import {
    buildPatientListQueryParams,
    normalizePhoneDigits,
    isPhoneSearchQuery,
} from "../../lib/patientListSearchParams";


import { MdDeleteOutline } from "react-icons/md";

import './Billing.css';

/** Normalize Mongo-style id or populated { _id, id } to string */
function normalizeId(val) {
    if (val == null || val === "") return "";
    if (typeof val === "object") return String(val._id || val.id || "");
    return String(val);
}

/** Best-effort doctor id from patient record (direct fields or latest visit). */
function resolveDoctorIdFromPatient(p) {
    if (!p) return "";
    const direct =
        p.doctorId ??
        p.primaryDoctorId ??
        p.primaryDoctor ??
        p.assignedDoctorId ??
        p.assignedDoctor;
    let id = normalizeId(direct);
    if (id) return id;
    const visits = Array.isArray(p.visitHistory) ? p.visitHistory : [];
    if (!visits.length) return "";
    const sorted = [...visits].sort(
        (a, b) =>
            new Date(b.visitDate || b.createdAt || 0) - new Date(a.visitDate || a.createdAt || 0)
    );
    for (const v of sorted) {
        const vid = normalizeId(v && v.doctorId);
        if (vid) return vid;
    }
    return "";
}

const LOOSE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function uploadInvoicePdf({ billId, pdfBase64, fileName }) {
    return axiosInstance.post(`/bills/${billId}/upload-invoice`, {
        pdfBase64,
        fileName,
    });
}

function blobToBase64DataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * POST /api/patients/search — JSON: branchId (required), optional name, phone, email.
 * Backend treats digits-only name as phone; we still send phone when we normalize digits.
 */
function buildPatientSearchPayload(branchId, q) {
    const trimmed = String(q || "").trim();
    const phoneDigits = normalizePhoneDigits(trimmed);
    const phoneSearch = isPhoneSearchQuery(trimmed);
    const emailSearch = LOOSE_EMAIL.test(trimmed);

    const payload = { branchId };

    if (emailSearch) {
        payload.email = trimmed;
        return {
            payload,
            phoneDigits,
            phoneSearch: false,
            emailSearch: true,
            nameSearch: false,
        };
    }

    if (phoneSearch && phoneDigits) {
        payload.phone = phoneDigits;
        // API: "Digits-only in name are treated as phone" — send name so that path matches too.
        payload.name = trimmed === phoneDigits ? phoneDigits : trimmed;
        return {
            payload,
            phoneDigits,
            phoneSearch: true,
            emailSearch: false,
            nameSearch: false,
        };
    }

    if (trimmed) {
        payload.name = trimmed;
    }
    return {
        payload,
        phoneDigits,
        phoneSearch: false,
        emailSearch: false,
        nameSearch: true,
    };
}

/**
 * GET /api/patients — branchId, page, limit; optional phone, name, registrationNumber (via buildPatientListQueryParams).
 */
function buildPatientListParams(branchId, q, meta) {
    const trimmed = String(q || "").trim();
    const base = { branchId, page: 1, limit: 50 };
    const { phoneDigits, phoneSearch } = meta;

    if (phoneSearch && phoneDigits) {
        return { ...base, phone: phoneDigits };
    }
    if (trimmed) {
        return { ...base, ...buildPatientListQueryParams(trimmed) };
    }
    return base;
}

/** Supports { items }, { data }, { patients }, or raw array — avoids empty UI when shape differs. */
function extractPatientItemsFromResponse(res) {
    const raw = res && res.data !== undefined ? res.data : res;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.patients)) return raw.patients;
    if (Array.isArray(raw.results)) return raw.results;
    return [];
}

function mergePatientLists(...lists) {
    const seen = new Set();
    const out = [];
    for (const list of lists) {
        if (!Array.isArray(list)) continue;
        for (const p of list) {
            const id = p && (p._id || p.id);
            const key = id != null ? String(id) : `idx-${out.length}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(p);
        }
    }
    return out;
}

const Billing = ({ branches = [], selectedBranchId = "", onBranchChange }) => {
    const [patientName, setPatientName] = useState('');
    const [doctorId, setDoctorId] = useState('');
    const [doctors, setDoctors] = useState([]);
    const [billDate, setBillDate] = useState(new Date().toISOString().slice(0, 10));
    const [patientPhone, setPatientPhone] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [patientRegistrationNumber, setPatientRegistrationNumber] = useState("");
    const [patientAge, setPatientAge] = useState("");
    const [disease, setDisease] = useState("");
    const [patientId, setPatientId] = useState('');
    const [patientSearchTerm, setPatientSearchTerm] = useState("");
    const [patientResults, setPatientResults] = useState([]);
    const [searchingPatients, setSearchingPatients] = useState(false);
    const [pendingDoctorId, setPendingDoctorId] = useState(null);
    const [doctorsReady, setDoctorsReady] = useState(false);
    const [services, setServices] = useState([]);
    const [discountRate] = useState(0);
    const invoiceCardRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [createdBillId, setCreatedBillId] = useState(null);
    const [createdBillStatus, setCreatedBillStatus] = useState(null);
    const [toastMessage, setToastMessage] = useState("");
    const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const branchLabel = (() => {
        const b = (branches || []).find((x) => (x._id || x.id) === selectedBranchId);
        return b ? (b.name || b.branchName || "—") : "—";
    })();
    const selectedDoctor = doctors.find((d) => String(d._id || d.id) === String(doctorId));
    const doctorName = selectedDoctor ? (selectedDoctor.name || "—") : "—";

    useEffect(() => {
        let mounted = true;
        async function loadDoctors() {
            if (!selectedBranchId) {
                if (mounted) {
                    setDoctors([]);
                    setDoctorId("");
                    setDoctorsReady(true);
                }
                return;
            }
            setDoctorsReady(false);
            try {
                const res = await axiosInstance.get("/doctor-branches", {
                    params: { branchId: selectedBranchId, isActive: true },
                });
                const data = res && res.data ? res.data : res;
                const maps = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                const rawList = maps.map((m) => m.doctorId).filter(Boolean);
                // De-dupe doctors by id (API can return duplicate mappings)
                const seen = new Set();
                const list = [];
                for (const d of rawList) {
                    const id = d && (d._id || d.id);
                    const key = id != null ? String(id) : "";
                    if (!key || seen.has(key)) continue;
                    seen.add(key);
                    list.push(d);
                }
                if (mounted) {
                    setDoctors(list);
                }
            } catch (err) {
                if (mounted) {
                    setDoctors([]);
                    setDoctorId("");
                }
            } finally {
                if (mounted) setDoctorsReady(true);
            }
        }
        loadDoctors();
        return () => {
            mounted = false;
        };
    }, [selectedBranchId]);

    // After patient pick: map doctor once branch doctor list has loaded (ids match reliably).
    useEffect(() => {
        if (pendingDoctorId == null) return;
        if (!selectedBranchId) {
            setPendingDoctorId(null);
            return;
        }
        if (!doctorsReady) return;
        const match = doctors.find((d) => String(d._id || d.id) === String(pendingDoctorId));
        if (match) {
            setDoctorId(String(match._id || match.id));
        }
        setPendingDoctorId(null);
    }, [doctors, doctorsReady, pendingDoctorId, selectedBranchId]);

    // PERFECT CALCULATION - Runs on every change
    const subtotal = services.reduce((sum, service) => sum + (service.total || 0), 0);
    const discountAmount = Math.round(subtotal * (discountRate / 100));
    const totalAmount = subtotal - discountAmount;

    const [savingInvoice, setSavingInvoice] = useState(false);
    const showToast = useCallback((message) => {
        setToastMessage(String(message || ""));
        setTimeout(() => setToastMessage(""), 2200);
    }, []);
    const waitForInvoiceRender = useCallback(async () => {
        // Ensure invoice number/id is painted before html2canvas snapshot.
        await new Promise((resolve) => setTimeout(resolve, 150));
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));
    }, []);
    const generateInvoicePdfBlob = useCallback(async () => {
        if (!invoiceCardRef.current) {
            throw new Error("Invoice card is not ready yet");
        }
        const canvas = await html2canvas(invoiceCardRef.current, {
            // Keep payload small enough for JSON/base64 invoice upload endpoint.
            scale: 1,
            useCORS: true,
            backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.72);
        const pdf = new jsPDF("p", "pt", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        if (imgHeight <= pageHeight) {
            pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
        } else {
            const ratio = pageHeight / imgHeight;
            pdf.addImage(imgData, "JPEG", 0, 0, imgWidth * ratio, imgHeight * ratio);
        }
        return pdf.output("blob");
    }, []);

    const uploadInvoiceToS3ForBill = useCallback(async (billId, billNumber) => {
        if (!billId) throw new Error("Bill id is required for invoice upload.");
        if (billNumber) setInvoiceNumber(String(billNumber));
        if (!createdBillId) setCreatedBillId(billId);
        await waitForInvoiceRender();
        const pdfBlob = await generateInvoicePdfBlob();
        const maxUploadBytes = 4 * 1024 * 1024;
        if (pdfBlob.size > maxUploadBytes) {
            throw new Error("Generated invoice PDF is too large to upload. Please reduce content and try again.");
        }
        const fileName = `Invoice-${billNumber || billId}.pdf`;
        const pdfBase64 = await blobToBase64DataUrl(pdfBlob);
        await uploadInvoicePdf({
            billId,
            pdfBase64,
            fileName,
        });
        return { pdfBlob, fileName };
    }, [createdBillId, generateInvoicePdfBlob, waitForInvoiceRender]);

    const addService = useCallback(() => {
        const newService = {
            id: Date.now(),
            name: '',
            qty: 1,
            rate: 0,
            total: 0
        };
        setServices(prev => [...prev, newService]);
    }, []);

    const updateService = useCallback((id, field, value) => {
        setServices(prev => prev.map(service => {
            if (service.id !== id) return service;

            const qty = field === 'qty' ? (parseInt(value) || 0) : service.qty;
            const rate = field === 'rate' ? (parseInt(value) || 0) : service.rate;

            return {
                ...service,
                [field]: field === 'qty' || field === 'rate' ? (parseInt(value) || 0) : value,
                qty,
                rate,
                total: qty * rate  // FIXED: Perfect calculation
            };
        }));
    }, []);

    const deleteService = useCallback((id) => {
        setServices(prev => prev.filter(service => service.id !== id));
    }, []);

    const searchPatients = useCallback(async (queryText = patientSearchTerm) => {
        const q = String(queryText || "").trim();
        if (!q) {
            setPatientResults([]);
            return;
        }
        if (!selectedBranchId) {
            setPatientResults([]);
            return;
        }
        const meta = buildPatientSearchPayload(selectedBranchId, q);
        const { payload } = meta;
        const listParams = buildPatientListParams(selectedBranchId, q, meta);
        setSearchingPatients(true);
        try {
            const [postRes, getRes] = await Promise.allSettled([
                axiosInstance.post("/patients/search", payload),
                axiosInstance.get("/patients", { params: listParams }),
            ]);
            const fromPost = postRes.status === "fulfilled" ? extractPatientItemsFromResponse(postRes.value) : [];
            const fromGet = getRes.status === "fulfilled" ? extractPatientItemsFromResponse(getRes.value) : [];
            const items = mergePatientLists(fromPost, fromGet);
            if (postRes.status === "rejected") {
                console.warn("POST /patients/search failed", postRes.reason);
            }
            if (getRes.status === "rejected") {
                console.warn("GET /patients failed", getRes.reason);
            }
            setPatientResults(items);
        } catch (err) {
            console.error("patient search failed", err);
            try {
                const res2 = await axiosInstance.get("/patients", { params: listParams });
                setPatientResults(extractPatientItemsFromResponse(res2));
            } catch (e2) {
                try {
                    const res3 = await axiosInstance.post("/patients/search", payload);
                    setPatientResults(extractPatientItemsFromResponse(res3));
                } catch (e3) {
                    setPatientResults([]);
                }
            }
        } finally {
            setSearchingPatients(false);
        }
    }, [patientSearchTerm, selectedBranchId]);

    useEffect(() => {
        const q = String(patientSearchTerm || "").trim();
        if (!q || !selectedBranchId) {
            setPatientResults([]);
            return undefined;
        }
        const timerId = setTimeout(() => {
            searchPatients(q);
        }, 300);
        return () => clearTimeout(timerId);
    }, [patientSearchTerm, selectedBranchId, searchPatients]);

    async function selectPatient(p) {
        const pid = p._id || p.id;
        setPatientId(pid);
        setPatientName(p.name || "");
        setPatientPhone(String(p.phone || ""));
        setPatientEmail(p.email || "");
        setPatientRegistrationNumber(p.registrationNumber || p.registrationNo || p.regNo || "");
        setPatientAge(p.age === 0 ? 0 : (p.age || ""));
        setPatientResults([]);
        setPatientSearchTerm("");

        let docId = resolveDoctorIdFromPatient(p);
        if (!docId && pid && selectedBranchId) {
            try {
                const res = await axiosInstance.get(`/patients/${pid}`, { params: { branchId: selectedBranchId } });
                const full = res && res.data ? res.data : res;
                docId = resolveDoctorIdFromPatient(full);
                if (full && typeof full === "object") {
                    setPatientRegistrationNumber(full.registrationNumber || full.registrationNo || full.regNo || "");
                    setPatientAge(full.age === 0 ? 0 : (full.age || ""));
                    if (!patientEmail) setPatientEmail(full.email || "");
                    if (!patientPhone) setPatientPhone(String(full.phone || ""));
                    if (!patientName) setPatientName(full.name || "");
                }
            } catch (e) {
                /* keep partial patient row */
            }
        }
        if (docId) {
            setPendingDoctorId(docId);
        }
    }

    const sendWhatsApp = async () => {
        if (!createdBillId) {
            alert("Please save the bill first.");
            return;
        }
        setSendingWhatsApp(true);
        try {
            const res = await axiosInstance.post(`/bills/${createdBillId}/send-whatsapp`, {});
            if (res.data && res.data.success) {
                showToast("WhatsApp message sent successfully!");
            } else {
                throw new Error("Failed to send WhatsApp message");
            }
        } catch (err) {
            console.error("WhatsApp send failed", err);
            const errMsg = (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Failed to send WhatsApp";
            alert(errMsg);
        } finally {
            setSendingWhatsApp(false);
        }
    };

    const downloadPDF = async () => {
        try {
            setSavingInvoice(true);
            let billId = createdBillId;
            let billNumber = invoiceNumber;
            if (!billId) {
                const result = await createBill(false);
                if (!result || !result.id) {
                    setSavingInvoice(false);
                    return;
                }
                billId = result.id;
                billNumber = result.billNumber;
            }
            const uploaded = await uploadInvoiceToS3ForBill(billId, billNumber || billId);
            const { pdfBlob, fileName } = uploaded;
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert((err && err.message) || "Failed to generate PDF");
        } finally {
            setSavingInvoice(false);
        }
    };

    async function createBill(showSuccessAlert = true) {
        if (!selectedBranchId) {
            alert("Please select a branch first.");
            return null;
        }
        if (!String(patientName || "").trim() || !String(patientPhone || "").trim()) {
            alert("Patient name and phone are required.");
            return null;
        }
        if (!String(patientId || "").trim()) {
            alert("Please select a patient (registration no. is required).");
            return null;
        }
        if (!services.length) {
            alert("Please add at least one bill item.");
            return null;
        }
        setSaving(true);
        try {
            // Ensure edits in Billing (age/registration/etc.) apply to the selected patient only.
            if (String(patientId || "").trim()) {
                try {
                    const updatePayload = {
                        branchId: selectedBranchId,
                        name: String(patientName || "").trim() || undefined,
                        phone: String(patientPhone || "").trim() || undefined,
                        email: String(patientEmail || "").trim() || undefined,
                        registrationNumber: String(patientRegistrationNumber || "").trim() || undefined,
                        age: patientAge === "" || patientAge == null ? undefined : Number(patientAge),
                    };
                    // remove empty object keys so backend doesn't overwrite with blanks
                    Object.keys(updatePayload).forEach((k) => {
                        if (updatePayload[k] === undefined) delete updatePayload[k];
                    });
                    await axiosInstance.put(`/patients/${patientId}`, updatePayload);
                } catch (e) {
                    // Don’t block billing if patient update fails, but inform the user.
                    console.warn("patient update before billing failed", e);
                }
            }

            const payload = {
                branchId: selectedBranchId,
                appointmentId: null,
                patient: {
                    name: String(patientName || "").trim(),
                    phone: String(patientPhone || "").trim(),
                    email: String(patientEmail || "").trim() || undefined,
                },
                disease: String(disease || "").trim() || undefined,
                items: services
                  .filter((s) => String(s.name || "").trim() && Number(s.qty) > 0 && Number(s.rate) >= 0)
                  .map((s) => ({ description: s.name, quantity: s.qty, unitPrice: s.rate })),
                paymentDetails: {
                  method: "cash",
                  paidAt: billDate ? `${billDate}T00:00:00.000Z` : new Date().toISOString(),
                },
            };
            if (!payload.patient.name || !payload.patient.phone) {
                alert("Patient name and phone are required.");
                setSaving(false);
                return null;
            }
            // mark bill as paid by default
            payload.status = "paid";
            const res = await axiosInstance.post("/bills/with-patient", payload);
            const data = res && res.data ? res.data : res;
            const bill = data && data.bill ? data.bill : data;
            const patient = data && data.patient ? data.patient : null;
            if (patient) {
              setPatientId(patient._id || patient.id || "");
              setPatientRegistrationNumber(patient.registrationNumber || patient.registrationNo || patient.regNo || "");
              setPatientAge(patient.age === 0 ? 0 : (patient.age || ""));
              setPatientName(patient.name || payload.patient.name);
              setPatientPhone(String(patient.phone || payload.patient.phone));
              setPatientEmail(patient.email || payload.patient.email || "");
            }
            setCreatedBillId(bill._id || bill.id);
            if (bill.billNumber) setInvoiceNumber(bill.billNumber);
            setCreatedBillStatus(bill.status || "paid");
            const savedBillId = bill._id || bill.id || null;
            const savedBillNumber = bill.billNumber || savedBillId;
            if (savedBillId) {
                try {
                    await uploadInvoiceToS3ForBill(savedBillId, savedBillNumber);
                } catch (uploadErr) {
                    const uploadMsg =
                        (uploadErr && uploadErr.response && uploadErr.response.data && uploadErr.response.data.error) ||
                        (uploadErr && uploadErr.message) ||
                        "Invoice upload failed";
                    alert(`Bill created, but invoice upload failed: ${uploadMsg}`);
                }
            }
            if (showSuccessAlert) showToast("Bill generated successfully");
            return { id: bill._id || bill.id, billNumber: bill.billNumber };
        } catch (err) {
            console.error("create bill failed", err);
            alert((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Create failed");
            return null;
        } finally {
            setSaving(false);
        }
    }


    return (
        <div className="billing-page">

            <Header1 branches={branches} selectedBranchId={selectedBranchId} onBranchChange={onBranchChange} />

            {/* Patient search (above billing container) */}
            <div className="billing-search-wrap">
                <div className="billing-search-row">
                    <input
                        value={patientSearchTerm}
                        onChange={(e) => setPatientSearchTerm(e.target.value)}
                        placeholder="Search patient by name / phone "
                        className="billing-search-input"
                    />
                    <button
                        className="billing-search-btn"
                        onClick={searchPatients}
                        disabled={searchingPatients}
                    >
                        {searchingPatients ? "Searching..." : "Search Patients"}
                    </button>
                </div>
                {patientResults.length > 0 && (
                    <div style={{ marginTop: 8, background: "#fff", border: "1px solid #e6e9ee", borderRadius: 8, maxWidth: 900 }}>
                        {patientResults.map(p => (
                            <div key={p._id || p.id} style={{ padding: 10, cursor: "pointer", borderBottom: "1px solid #f3f4f6" }} onClick={() => selectPatient(p)}>
                                <strong>{p.name}</strong> {p.phone ? `· ${p.phone}` : ""} {p.email ? `· ${p.email}` : ""}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="billing-container">
                {/* Left - Billing */}
                <div className="billing-left">
                    <div className="billing-card">

                        {/* Header */}
                        <div className="bill-header">
                            <h2>Bill Details</h2>
                        </div>

                        {/* Patient Info */}
                        <div className="patient-info">
                            <div className="patient-field">
                                <label>Patient Name</label>
                                <input
                                    value={patientName}
                                    onChange={(e) => setPatientName(e.target.value)}
                                    placeholder="Enter patient name"
                                />
                            </div>

                            <div className="patient-field">
                                <label>Phone Number</label>
                                <input
                                    value={patientPhone}
                                    onChange={(e) => setPatientPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                    placeholder="Enter phone number"
                                />
                            </div>

                            <div className="patient-field">
                                <label>Email</label>
                                <input
                                    value={patientEmail}
                                    onChange={(e) => setPatientEmail(e.target.value)}
                                    placeholder="Enter email (optional)"
                                />
                            </div>

                            <div className="patient-field">
                                <label>Age</label>
                                <input
                                    value={patientAge}
                                    type="number"
                                    inputMode="numeric"
                                    min="0"
                                    placeholder="Age"
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === "") {
                                            setPatientAge("");
                                            return;
                                        }
                                        const n = Number(raw);
                                        setPatientAge(Number.isFinite(n) ? n : "");
                                    }}
                                />
                            </div>

                            <div className="patient-field">
                                <label>Doctor Name</label>
                                <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
                                    <option value="">Select doctor</option>
                                    {doctors.map((d) => (
                                        <option key={d._id || d.id} value={d._id || d.id}>
                                            {d.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="patient-field">
                                <label>Registration No</label>
                                <input
                                    value={patientRegistrationNumber}
                                    placeholder="REG-XXXX"
                                    onChange={(e) => setPatientRegistrationNumber(e.target.value)}
                                />
                            </div>
                            <div className="patient-field">
                                <label>Bill Date</label>
                                <input
                                    type="date"
                                    value={billDate}
                                    onChange={(e) => setBillDate(e.target.value)}
                                />
                            </div>

                            <div className="patient-field" style={{ gridColumn: "1 / -1" }}>
                                <label>Disease</label>
                                <input
                                    value={disease}
                                    onChange={(e) => setDisease(e.target.value)}
                                    placeholder="Enter disease (optional)"
                                />
                            </div>
                        {/* search moved above */}
                        </div>

                        {/* Services / Medicines */}
                        <div className="services-section">
                            <div className="services-header">
                                <h3>Services / Medicines</h3>
                            </div>

                            <div className="service-columns-header">
                                <span>Item</span>
                                <span className="col-qty">QTY</span>
                                <span className="col-price">Price</span>
                                <span className="col-amount">Amount</span>
                                <span className="col-action"></span>
                            </div>

                            <div className="services-list">
                                {services.map((service) => (
                                    <div key={service.id} className="service-item">
                                        <input
                                            className="service-name-input"
                                            value={service.name}
                                            onChange={(e) =>
                                                updateService(service.id, "name", e.target.value)
                                            }
                                            placeholder="Consultation / Medicine"
                                        />

                                        <div className="service-controls">
                                            <input
                                                className="qty-input"
                                                type="number"
                                                value={service.qty}
                                                min="1"
                                                inputMode="numeric"
                                                onChange={(e) =>
                                                    updateService(service.id, "qty", e.target.value)
                                                }
                                            />

                                            <input
                                                className="rate-input"
                                                type="number"
                                                value={service.rate === 0 ? "" : service.rate}
                                                min="0"
                                                inputMode="numeric"
                                                onChange={(e) =>
                                                    updateService(service.id, "rate", e.target.value)
                                                }
                                            />

                                            <span className="service-total">
                                                ₹{service.total.toLocaleString()}
                                            </span>

                                            <button
                                                className="delete-btn"
                                                onClick={() => deleteService(service.id)}
                                            >
                                                <MdDeleteOutline />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                                <button className="add-btn" onClick={addService}>
                                    + Add Item
                                </button>
                            </div>
                        </div>

                        {/* TOTAL AMOUNT ONLY */}
                        <div className="total-amount-section">
                            <span className="total-label">Total Amount</span>
                            <span className="total-value">
                                ₹{totalAmount.toLocaleString()}
                            </span>
                        </div>

                    </div>
                </div>


                {/* Right - Invoice */}
                <div className="billing-right">
                    <div className="invoice-card simple-invoice" ref={invoiceCardRef}>

                        {/* Invoice Header */}
                        <div className="simple-invoice-header">
                            <div className="clinic-title">
                                <img src="/clinic-logo.png" alt="Clinic logo" className="clinic-logo-img" />
                            </div>

                            <div className="invoice-title">
                                <h1>INVOICE</h1>
                                <p># {invoiceNumber || (createdBillId && String(createdBillId).length < 20 ? createdBillId : "----")}</p>
                            </div>
                        </div>

                        <div className="divider-line"></div>
                        <div className="invoice-meta-grid">
                            <div><strong>Patient:</strong> {patientName || "-"}</div>
                            <div><strong>Doctor:</strong> {doctorName || "-"}</div>
                            <div><strong>Branch:</strong> {branchLabel}</div>
                            <div><strong>Date:</strong> {billDate || "-"}</div>
                            <div><strong>Phone:</strong> {patientPhone || "-"}</div>
                            <div><strong>Email:</strong> {patientEmail || "-"}</div>
                            <div><strong>Age:</strong> {patientAge !== "" && patientAge != null ? patientAge : "-"}</div>
                            <div><strong>Registration No:</strong> {patientRegistrationNumber || "-"}</div>
                            <div style={{ gridColumn: "1 / -1" }}><strong>Disease:</strong> {String(disease || "").trim() || "-"}</div>
                        </div>

                        {/* Table Header */}
                        <div className="invoice-table-header">
                            <span>DESCRIPTION</span>
                            <span className="amount-col qty-col">QTY</span>
                            <span className="amount-col price-col">PRICE</span>
                            <span className="amount-col">AMOUNT</span>
                        </div>

                        {/* Invoice Items */}
                        <div className="invoice-items">
                            {services.length === 0 ? (
                                <div className="invoice-item-row">
                                    <span style={{ color: "#9ca3af" }}>No items added</span>
                                    <span className="amount-col qty-col" style={{ color: "#9ca3af" }}>-</span>
                                    <span className="amount-col price-col" style={{ color: "#9ca3af" }}>₹ 0</span>
                                    <span className="amount-col" style={{ color: "#9ca3af" }}>₹ 0</span>
                                </div>
                            ) : (
                                services.map((service) => (
                                    <div key={service.id} className="invoice-item-row">
                                        <span>{service.name || "Service"}</span>
                                        <span className="amount-col qty-col">
                                            {service.qty || 0}
                                        </span>
                                        <span className="amount-col price-col">
                                            ₹ {Number(service.rate || 0).toLocaleString()}
                                        </span>
                                        <span className="amount-col">
                                            ₹ {service.total.toLocaleString()}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Grand Total */}
                        <div className="invoice-grand-total">
                            <span>Grand Total</span>
                            <span className="grand-total-amount">
                                ₹ {totalAmount.toLocaleString()}
                            </span>
                        </div>

                        <div className="invoice-note">
                            Thank you for choosing Dr. Anubbhas Homeopathy. Get well soon.
                        </div>
                    </div>

                    {/* UI-only controls (kept out of invoice PDF capture) */}
                    <div className="invoice-ui-controls">
                        {createdBillId && (
                            <div className="invoice-created-meta">
                                <div>Created bill: <strong>{createdBillId}</strong></div>
                                <div className="invoice-status-pill">
                                    {createdBillStatus ? createdBillStatus.toUpperCase() : "PAID"}
                                </div>
                            </div>
                        )}

                        <div className="invoice-actions">
                            {!createdBillId && (
                                <button className="create-btn" onClick={createBill} disabled={saving}>
                                    {saving ? "Saving..." : "Save Bill"}
                                </button>
                            )}
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setServices([]);
                                    setInvoiceNumber("");
                                    setCreatedBillId(null);
                                    setCreatedBillStatus(null);
                                    setDisease("");
                                }}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>

            </div>
            <div className="billing-footer">
                <div className="footer-center">
                    <button className="pdf-btn" onClick={downloadPDF} disabled={savingInvoice}>
                        <span><BsDownload /></span> {savingInvoice ? "Saving..." : "Download PDF"}
                    </button>
                </div>

                <div className="footer-right">
                    <button 
                        className="whatsapp-btn" 
                        onClick={sendWhatsApp} 
                        disabled={sendingWhatsApp || !createdBillId}
                    >
                        <span><FaWhatsapp /></span> 
                        {sendingWhatsApp ? "Sending..." : "WhatsApp"}
                    </button>

                    <button className="print-btn" onClick={downloadPDF} disabled={savingInvoice}>
                        <span><IoPrint />
                        </span> {savingInvoice ? "Saving..." : "Print & Save"}
                    </button>
                </div>
            </div>
            {toastMessage && <div className="billing-toast">{toastMessage}</div>}
        </div>
    );
};

export default Billing;
